import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Accordion } from 'react-bootstrap';
import { MdBuild } from 'react-icons/md';
import { SettingsContext } from '../SettingsContext';
import DataTable from 'react-data-table-component';
import {
    fetchOrgManufacturers,
    fetchServiceProviderCategoriesByManufacturer,
    fetchServiceProviders,
} from '../../services/Http/Api';
import PageContainer from '../../shared/layout/containers/PageContainer';
import PageHeader from '../../shared/layout/headers/PageHeader';
import PageTitle from '../../shared/layout/titles/PageTitle';
import PageContent from '../../shared/layout/containers/PageContent';
import MainButton from '../../shared/layout/buttons/MainButton';
import StyledCard from '../../shared/layout/cards/StyledCard';
import LoadingMessage from '../../shared/layout/feedback/LoadingMessage';
import EmptyState from '../../shared/layout/feedback/EmptyState';
import { SimpleSelect } from '../../shared/layout/forms/AppSelect';
import {
    StyledLabel,
    FormGroup,
    FormFooter,
    ErrorText
} from '../../shared/layout/forms/FormElements';

const RepairSettingsPage = () => {
    const { t } = useTranslation();
    const { siteSettings, loading: siteLoading } = useContext(SettingsContext);
    const [manufacturers, setManufacturers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [serviceProviders, setServiceProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: {
            manufacturer_id: '',
            category_id: '',
        },
    });

    const selectedManufacturer = watch('manufacturer_id');

    useEffect(() => {
        if (siteLoading) return;
        const loadInitialData = async () => {
            try {
                const manufacturersData = await fetchOrgManufacturers({ withServiceProviders: true });
                setManufacturers(manufacturersData);
                setLoading(false);
            } catch (err) {
                toast.error(t('Settings.RepairServices.error') + ': ' + err.message);
                setLoading(false);
            }
        };
        loadInitialData();
    }, [t, siteLoading]);

    useEffect(() => {
        if (selectedManufacturer) {
            const loadCategories = async () => {
                try {
                    const categoriesData = await fetchServiceProviderCategoriesByManufacturer(selectedManufacturer);
                    setCategories(categoriesData);
                    setServiceProviders([]);
                    reset({ ...watch(), category_id: '' });
                } catch (err) {
                    toast.error(t('Settings.RepairServices.error') + ': ' + err.message);
                }
            };
            loadCategories();
        } else {
            setCategories([]);
            setServiceProviders([]);
        }
    }, [selectedManufacturer, reset, t]);

    const onSearchSubmit = async (data) => {
        try {
            const providers = await fetchServiceProviders({
                manufacturer_id: data.manufacturer_id,
                category_id: data.category_id
            });
            setServiceProviders(providers);
        } catch (err) {
            toast.error(t('Settings.RepairServices.error') + ': ' + err.message);
        }
    };

    const columns = [
        { name: t('Settings.RepairServices.serviceProvider'), selector: row => row.serviceName, sortable: true },
        { name: t('Settings.RepairServices.venueName'), selector: row => row.name, sortable: true },
        { name: t('Settings.RepairServices.address'), selector: row => `${row.address}, ${row.city}`, sortable: true },
        { name: t('Settings.RepairServices.phone'), selector: row => row.phone || 'N/A', sortable: true },
    ];

    // Flatten provider data for DataTable
    const tableData = serviceProviders.flatMap(provider =>
        provider.venues.map(venue => ({
            ...venue,
            serviceName: provider.service_provider_name
        }))
    );

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '52px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb !important',
                    transition: 'background-color 0.2s',
                },
            },
        },
    };

    if (loading || siteLoading) return <LoadingMessage>{t('Settings.loading')}</LoadingMessage>;

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdBuild size={28} />}>{t('Settings.RepairServices.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                <StyledCard noPadding>
                    <StyledAccordion defaultActiveKey="0" flush>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>{t('Settings.RepairServices.search')}</Accordion.Header>
                            <Accordion.Body>
                                <Form onSubmit={handleSubmit(onSearchSubmit)}>
                                    <FormGroup>
                                        <StyledLabel>{t('Settings.RepairServices.selectBrand')}</StyledLabel>
                                        <Controller
                                            name="manufacturer_id"
                                            control={control}
                                            rules={{ required: t('Settings.RepairServices.brandRequired') }}
                                            render={({ field }) => (
                                                <SimpleSelect
                                                    inputId="manufacturer_id"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    options={[
                                                        { value: '', label: t('Settings.RepairServices.selectBrand') },
                                                        ...manufacturers.map((manufacturer) => ({
                                                            value: manufacturer.id,
                                                            label: manufacturer.name,
                                                        })),
                                                    ]}
                                                />
                                            )}
                                        />
                                        {errors.manufacturer_id && <ErrorText>{errors.manufacturer_id.message}</ErrorText>}
                                    </FormGroup>
                                    <FormGroup>
                                        <StyledLabel>{t('Settings.RepairServices.selectCategory')}</StyledLabel>
                                        <Controller
                                            name="category_id"
                                            control={control}
                                            rules={{ required: t('Settings.RepairServices.categoryRequired') }}
                                            render={({ field }) => (
                                                <SimpleSelect
                                                    inputId="category_id"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    isDisabled={!selectedManufacturer}
                                                    options={[
                                                        { value: '', label: t('Settings.RepairServices.selectCategory') },
                                                        ...categories.map((category) => ({
                                                            value: category.id,
                                                            label: category.path || category.name,
                                                        })),
                                                    ]}
                                                />
                                            )}
                                        />
                                        {errors.category_id && <ErrorText>{errors.category_id.message}</ErrorText>}
                                    </FormGroup>
                                    <FormFooter>
                                        <MainButton type="submit">{t('Settings.RepairServices.search')}</MainButton>
                                    </FormFooter>
                                </Form>

                                {serviceProviders.length > 0 && (
                                    <div style={{ marginTop: '20px' }}>
                                        <DataTable
                                            columns={columns}
                                            data={tableData}
                                            pagination
                                            customStyles={customStyles}
                                        />
                                    </div>
                                )}
                                {serviceProviders.length === 0 && <EmptyState>{t('Settings.RepairServices.noRepairCompanies')}</EmptyState>}
                            </Accordion.Body>
                        </Accordion.Item>
                    </StyledAccordion>
                </StyledCard>
            </PageContent>
        </PageContainer>
    );
};

export default RepairSettingsPage;

const StyledAccordion = styled(Accordion)`
    .accordion-button {
        color: #1e293b;
        font-weight: 600;
        background-color: transparent;
        box-shadow: none;
        padding: 1rem 1.25rem;
        
        &:not(.collapsed) {
            color: #821692;
            background-color: #fdf4ff;
        }
        
        &:focus {
            box-shadow: none;
            border-color: rgba(130, 22, 146, 0.1);
        }
    }
    .accordion-body {
        padding: 1.5rem;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    max-width: 600px;
`;
